import type { WorldDateView } from "../world/types";
import { TICK_HZ } from "./timestep";

/*
 * Crossing a day at speed: the same ticks, run without the frames.
 *
 * Two things in this client want the world to move faster than a player can
 * watch it, and plan decision 8 is explicit that both must do it the same way:
 * **every tick genuinely runs.** The sleep verb waits for morning and the dev
 * skip crosses quiet days, and neither one advances a clock, edits a date or
 * asks the simulation to jump — they call `world.step()` in a loop, in the same
 * order the host loop calls it, so every command lands on its true tick and the
 * replay of a session that slept is bit-identical to the replay of a session
 * that sat up all night. That is the whole reason this file exists rather than
 * a `setDate`.
 *
 * # Why it is a class and not a loop
 *
 * Because a day no longer fits in a frame. Under the old calendar a `for` loop
 * crossed one between two repaints; a twenty-minute day is thirty times as many
 * ticks (plan decision 7), and a loop that ran it all at once would be a third
 * of a second of frozen tab per day crossed. So a crossing is *state*: it is
 * opened, advanced against a deadline as many frames as it takes, and closed
 * when the date turns. The caller keeps drawing in between, which is what lets
 * the sleep verb fade the screen down and back up over a night that genuinely
 * happened.
 *
 * # Everything arrives as a parameter
 *
 * The world, the date and the clock are all injected ({@link FastForwardHost}),
 * for the reason `host/timestep.ts` gives about its own clock: it is what lets
 * `dev/selfcheck.ts` drive a day, a budget and a runaway world instantly and
 * with no flakiness. Nothing here imports the simulation.
 */

/**
 * The most ticks one day may take before a crossing gives up.
 *
 * **A guard, not the number.** How many ticks a day is, is the simulation's
 * business and the client deliberately owns no calendar arithmetic — it steps
 * until the *date* changes (`world/types.ts`, `WorldDateView`). What this stops
 * is the spin: a session with no history is a session whose date never turns,
 * and without a bound the skip loops would run until the tab was closed.
 *
 * The size of it is *derived rather than picked*, and the derivation is checked
 * in `dev/selfcheck.ts` so it fails loudly if either end drifts: the client
 * refuses to believe in a day longer than `DAY_SECONDS_MAX` seconds of stepped
 * time (`witness/witness.ts`), which at {@link TICK_HZ} is seventy-two thousand
 * ticks — so a guard below that could refuse to cross a day the same client is
 * perfectly happy to measure. Ninety thousand clears it by a quarter. Against
 * the twenty-minute day the owner's pacing actually asks for it is two and a
 * half times over.
 */
export const MAX_TICKS_PER_DAY = 90_000;

/**
 * How many ticks are run between two readings of the clock.
 *
 * `performance.now()` is tens of nanoseconds and a tick is microseconds, so
 * reading it every tick would be a percent or so of the budget spent asking
 * what time it is — small, but it is the one cost here that buys nothing.
 * Sixty-four ticks is at most a couple of milliseconds of overshoot past a
 * deadline, and it has a second use: it guarantees a crossing makes progress
 * even when it is handed a deadline that has already passed, which is what
 * keeps a slow frame from stalling a sleep forever.
 */
const TICKS_PER_CLOCK_READ = 64;

/** What stopped a crossing. */
export type DayOutcome =
  /** The date turned. The crossing is closed and the caller has its morning. */
  | "morning"
  /** The frame's budget ran out. The crossing stays open; call again next frame. */
  | "budget"
  /** {@link MAX_TICKS_PER_DAY} ticks and no new day. Something is wrong; the crossing is closed. */
  | "guard"
  /** The session has no calendar to turn. The crossing is closed. */
  | "nocalendar";

/**
 * The four things a crossing needs from the client, and nothing else: something
 * to step, a date, a clock, and — since chunk Fx-7 — somewhere to say the player
 * is lying down ({@link FastForwardHost.stand}, the only one that is optional).
 */
export interface FastForwardHost {
  /**
   * One fixed step of the whole client: the interest command, the world, and
   * the witnessing over it — in that order, exactly as the host loop's `step`
   * does it, so every day boundary the client would have noticed is noticed.
   */
  tick(): void;

  /** The world's date, or `null` in a session with no history to keep one. */
  date(): WorldDateView | null;

  /** Wall-clock milliseconds. `performance.now` in a session; a fake in the checks. */
  now(): number;

  /**
   * States where the player is standing, once, as the crossing opens.
   *
   * **The fourth thing, and it is the one chunk Fx-2 filed and Fx-7 fixed**: a
   * crossing runs the world with nothing being drawn, and the local tier's
   * bodies are re-read where the scene is *drawn* — so the trigger that tells
   * the simulation where the player is (`witness/witness.ts`'s `standIfWatched`)
   * never fires for the length of a night. A wanted player who camped beside a
   * guard was unreachable until morning.
   *
   * One statement, at the opening, which is exactly the right amount while
   * nothing moves the player: a body lying still is a body whose stated tile
   * stays true, and the walk that got them here is the only thing that could
   * have made the last one stale (`host/rest.ts`'s `RestWorld.stand` says the
   * same sentence about the other two crossings, and in a session it *is* the
   * same call).
   *
   * **Optional**, because a crossing does not always have a body in it: the fake
   * worlds the checks drive have nobody standing anywhere, and neither does a
   * world with no witnessing layer yet. Nothing here decides whether a command
   * goes out — the trigger does, and it is silent unless somebody in the town
   * needs the tile.
   */
  stand?(): void;
}

/**
 * One day being crossed, over as many frames as it takes.
 *
 * Held open across frames rather than run to completion, so the caller keeps
 * drawing while the world runs — see the header. Not reentrant and not meant to
 * be: one instance is one thing crossing one day.
 */
export class FastForward {
  private readonly host: FastForwardHost;

  /** The date the crossing started from, or `null` when nothing is crossing. */
  private from: WorldDateView | null = null;

  /** Ticks run since the crossing opened. Spans frames; see {@link MAX_TICKS_PER_DAY}. */
  private spent = 0;

  constructor(host: FastForwardHost) {
    this.host = host;
  }

  /** The date being crossed away from, or `null` when idle. */
  get crossing(): WorldDateView | null {
    return this.from;
  }

  /** How many ticks the open crossing has run. Zero when idle. */
  get ticks(): number {
    return this.spent;
  }

  /**
   * Opens a crossing from whatever day the world is standing in, and states
   * where the player is standing as it does.
   *
   * `false` when there is no calendar to cross — a world with no history
   * (`?bootstrap=0`), where the date is `null` and nothing is watching it. The
   * caller says so out loud rather than spinning, and nothing is stated: a
   * crossing that did not open is a night nobody spent anywhere.
   *
   * The stand is here rather than at the key press because the crossing opens
   * when the screen has finished going dark, and that fade is six tenths of a
   * second of walking ({@link FastForwardHost.stand}, `main.ts`'s `stepCamp`).
   */
  start(): boolean {
    const today = this.host.date();

    if (today === null) {
      return false;
    }

    this.from = today;
    this.spent = 0;
    this.host.stand?.();

    return true;
  }

  /**
   * Runs ticks until the date turns, the guard trips, or `deadline` passes.
   *
   * `deadline` is a reading of {@link FastForwardHost.now} in the future —
   * absolute rather than a duration, so a caller crossing several days in one
   * frame spends one budget between them all rather than one each.
   *
   * The date is checked after every tick and the clock only every
   * {@link TICKS_PER_CLOCK_READ}: stopping *on* the boundary is the whole
   * point of the call, and stopping within a millisecond of a budget is close
   * enough for something whose next act is to draw a frame.
   */
  advance(deadline: number): DayOutcome {
    const from = this.from;

    if (from === null) {
      return "nocalendar";
    }

    for (;;) {
      for (let batch = 0; batch < TICKS_PER_CLOCK_READ; batch += 1) {
        if (this.spent >= MAX_TICKS_PER_DAY) {
          // A day the client cannot cross. Given up on rather than pushed
          // through, and said out loud by the caller: a world whose day is
          // longer than the guard is a world this build does not understand,
          // and quietly running forever would hide that.
          return this.close("guard");
        }

        this.host.tick();
        this.spent += 1;

        const now = this.host.date();

        if (now === null) {
          return this.close("nocalendar");
        }

        if (now.year !== from.year || now.dayOfYear !== from.dayOfYear) {
          return this.close("morning");
        }
      }

      if (this.host.now() >= deadline) {
        return "budget";
      }
    }
  }

  /** Abandons an open crossing. The world keeps whatever ticks it already ran. */
  cancel(): void {
    this.from = null;
    this.spent = 0;
  }

  private close(outcome: DayOutcome): DayOutcome {
    this.from = null;
    this.spent = 0;

    return outcome;
  }
}

/**
 * Runs a fixed span of stepped time, drawing nothing. Returns the ticks it ran.
 *
 * The sibling of a day crossing, for a caller that wants *less* than a day:
 * a headless driver photographing a staged reaction, which lasts ninety seconds
 * of stepped time and would be over long before a twenty-minute day was
 * (`witness/staging.ts`, `dev/handle.ts`). Seconds rather than ticks because
 * everything else the client paces is in seconds of stepped time, and
 * {@link TICK_HZ} is the client's own number rather than the calendar's.
 *
 * Bounded by the same guard a crossing is, so a driver that asks for an hour
 * gets a day's worth and a tab that is still responsive.
 */
export function runStepped(host: FastForwardHost, seconds: number): number {
  const wanted = Number.isFinite(seconds) ? Math.round(seconds * TICK_HZ) : 0;
  const ticks = Math.min(Math.max(wanted, 0), MAX_TICKS_PER_DAY);

  for (let tick = 0; tick < ticks; tick += 1) {
    host.tick();
  }

  return ticks;
}
